import React, { useState, useEffect, useMemo } from 'react'
import { Routes, Route } from 'react-router-dom'
import { NotFound } from 'components/core-ui'
import { Home } from '../../Home'
import { LoginOIDC } from '../../LoginOIDC'
import { LogoutOIDC } from '../../LogoutOIDC'
import { ServiceDomain } from '../../ServiceDomain'
import { SHOP_RECEPTION_ALL_MODULE_IDS, URLS } from 'utils/constants'
import { Layout } from 'components/Layout'
import { ThemeProvider } from '@fullscreendigital/carrefour-dex-fe'
import { createRoute } from '../helpers'
import { makeDesignerGetRequest, makeCorePutRequest } from 'utils/api'
import { PrivateRoute } from 'components/utils/PrivateRoute'
import { toApplicationPages } from '../serializers'
import {
  setFirebaseToken,
  getUserPreferredName,
  setFirebaseUser,
  getUserRoles
} from 'utils/authService'
import { useNotification } from 'hooks'
import { toApplicationModulesPages } from 'components/Layout/Sidebar/serializers'
import { CircularProgress } from '@mui/material'
import { formatErrorMessage } from 'utils/formatting'
import { useGovernanceRoutes } from 'components/governance/routes'
import { useVolumetryRoutes } from 'components/volumetry/routes'
import { useBakeryRoutes } from 'components/bakery/routes'
import { useSupplyOmniRoutes } from 'components/supplyomni/routes'
import BackButtonTitle from 'components/core-ui/BackButtonTitle/BackButtonTitle'
import { useMerchandiseRoutes } from '../../shop-reception/mobile/routes'
import { useShopReceptionRoutes } from '../../shop-reception/admin/routes'
import { getFBToken } from 'utils/firebase'
import { useLostSalesOpportunitiesRoutes } from 'components/lostSalesOpportunities/routes'
import { useLocationRoutes } from 'components/location/routes'
import { useWasteManagementRoutes } from 'components/waste-management/routes'
import { useEmployeeRoutes } from 'components/employee/routes'
import { useIamRoutes } from 'components/iam/routes'
import { useCoraSGRRoutes } from 'components/cora-sgr-coupons/routes'
import { useEmployeeDiscountsRoutes } from '../../employee-discounts/routes'

import { getShopReceptionShops } from 'components/Layout/api'
import { SHOP_RECEPTION_FETCH_SHOP_ROLES, SHOP } from 'components/Layout/constants'
import { isAllowed } from 'utils/helpers'
import { ShopReceptionNoAssociationModal } from 'components/Layout/ShopReceptionNoAssociationModal'

const AppInner = () => {
  const [modules, setModules] = useState([])
  const [applicationPages, setApplicationPages] = useState([])
  const [applicationModulesPages, setApplicationModulesPages] = useState([])
  const [subdomainId, setSubdomainId] = useState(localStorage.getItem('selectedSubdomainId'))
  const [allScreensRoutes, setAllScreensRoutes] = useState([])

  const [isLoadingAppModules, setIsLoadingAppModules] = useState(false)
  const [hasLoadedAppModules, setHasLoadedAppModules] = useState(false)
  const [isLoadingActivities, setIsLoadingActivities] = useState(false)
  const [hasLoadedActivities, setHasLoadedActivities] = useState(-1) // [~] holds the subdomainId for which the activities were loaded
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(true)

  const [fbToken, setFbToken] = useState(null)

  const { showNotification } = useNotification()

  useEffect(() => {
    ;(async () => {
      await getFBToken(setFbToken)
    })()
  }, [])

  useEffect(() => {
    ;(async () => {
      // const localToken = getFirebaseToken()
      // const firebaseUser = getFirebaseUser()
      const loggedUser = getUserPreferredName()
      // if (fbToken && (fbToken !== localToken || firebaseUser !== loggedUser)) {
      if (fbToken) {
        try {
          await makeCorePutRequest('/message-dispatcher/external/association', {
            deviceToken: fbToken
          })
          setFirebaseToken(fbToken)
          setFirebaseUser(loggedUser)
        } catch (error) {
          console.log(error)
        }
      }
    })()
  }, [fbToken])

  useEffect(() => {
    // console.log("[App] 1st effect called | hasLoadedAppModules: ", hasLoadedAppModules)
    ;(async () => {
      if (!hasLoadedAppModules && !isLoadingAppModules) {
        setIsLoadingAppModules(true)

        try {
          // console.log("[App] will load app modules")
          const result = await makeDesignerGetRequest('/application-modules')
          setModules(result)
          setApplicationModulesPages(toApplicationModulesPages(result))
        } catch (error) {
          // https://giphy.com/embed/oe33xf3B50fsc :))))
          showNotification({
            message: `Error while fetching application modules. ${formatErrorMessage(error)}`,
            type: 'error'
          })
        } finally {
          setHasLoadedAppModules(true)
          setIsLoadingAppModules(false)
          // console.log("[App] did load app modules")
        }
      }
    })()
  }, [hasLoadedAppModules, isLoadingAppModules])

  useEffect(() => {
    ;(async () => {
      if (
        hasLoadedAppModules &&
        !isLoadingAppModules &&
        subdomainId &&
        subdomainId !== 'undefined' &&
        subdomainId != hasLoadedActivities &&
        !isLoadingActivities
      ) {
        setIsLoadingActivities(true)

        try {
          const result = await makeDesignerGetRequest(`/activities?submoduleId=${subdomainId}`)
          setApplicationPages(toApplicationPages(result))
        } catch (error) {
          showNotification({
            message: `Error while fetching application pages. ${formatErrorMessage(error)}`,
            type: 'error'
          })
        } finally {
          setHasLoadedActivities(subdomainId)
          setIsLoadingActivities(false)
        }
      }
    })()
  }, [
    subdomainId,
    hasLoadedAppModules,
    isLoadingAppModules,
    hasLoadedActivities,
    isLoadingActivities
  ])

  useEffect(() => {
    ;(async () => {
      try {
        const result = await makeDesignerGetRequest('/screens/route-urls')
        setAllScreensRoutes(result)
      } catch (error) {
        showNotification({
          message: `Error while fetching application pages. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      } finally {
        setIsLoadingRoutes(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const roles = useMemo(() => getUserRoles(), [])
  const checkShop = !!(roles && isAllowed(SHOP_RECEPTION_FETCH_SHOP_ROLES, roles))

  const { data: shops, isLoading: shopsLoading } = getShopReceptionShops(
    showNotification,
    checkShop
  )

  const { shopReceptionDisabled, noAssociationModalInfo } = useMemo(() => {
    if (!checkShop) {
      return { shopReceptionDisabled: false, noAssociationModalInfo: { display: false } }
    }
    if (checkShop && Array.isArray(shops) && shops.length === 0) {
      return { shopReceptionDisabled: true, noAssociationModalInfo: { display: true, type: SHOP } }
    }

    return { shopReceptionDisabled: false, noAssociationModalInfo: { display: false } }
  }, [checkShop, shops])

  // show the info modal once when disabled
  const [showNoAssocModal, setShowNoAssocModal] = useState(false)
  useEffect(() => {
    if (!shopsLoading && shopReceptionDisabled) setShowNoAssocModal(true)
  }, [shopsLoading, shopReceptionDisabled])

  const modulesWithDisabled = useMemo(() => {
    if (!shopReceptionDisabled) return modules
    const deny = new Set(SHOP_RECEPTION_ALL_MODULE_IDS)
    return (modules || []).map(m => (deny.has(m.id) ? { ...m, disabled: true } : m))
  }, [modules, shopReceptionDisabled])

  const applicationModulesPagesWithDisabled = useMemo(() => {
    if (!shopReceptionDisabled) return applicationModulesPages
    const deny = new Set(SHOP_RECEPTION_ALL_MODULE_IDS)
    return (applicationModulesPages || []).map(p => (deny.has(p.id) ? { ...p, disabled: true } : p))
  }, [applicationModulesPages, shopReceptionDisabled])

  const governanceRoutes = useGovernanceRoutes(allScreensRoutes)
  const volumetryRoutes = useVolumetryRoutes(allScreensRoutes)
  const bakeryRoutes = useBakeryRoutes(allScreensRoutes)
  const supplyOmniRoutes = useSupplyOmniRoutes(allScreensRoutes)
  const merchandiseRoutes = useMerchandiseRoutes(allScreensRoutes)
  const shopReceptionRoutes = useShopReceptionRoutes(allScreensRoutes)
  const lostSalesOpportunitiesRoutes = useLostSalesOpportunitiesRoutes(allScreensRoutes)
  const locationRoutes = useLocationRoutes(allScreensRoutes)
  const wasteManagementRoutes = useWasteManagementRoutes(allScreensRoutes)
  const employeeRoutes = useEmployeeRoutes(allScreensRoutes)
  const iamRoutes = useIamRoutes(allScreensRoutes)
  const coraSGRRoutes = useCoraSGRRoutes(allScreensRoutes)
  const employeeDiscountsRoutes = useEmployeeDiscountsRoutes(allScreensRoutes)

  const appStillLoading = useMemo(
    () => isLoadingAppModules || !hasLoadedAppModules || isLoadingRoutes,
    [isLoadingAppModules, hasLoadedAppModules, isLoadingRoutes]
  )

  return (
    <ThemeProvider theme="light">
      {appStillLoading ? (
        <div style={{ display: 'flex' }}>
          <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
        </div>
      ) : (
        <>
          {shopReceptionDisabled && noAssociationModalInfo.display && (
            <ShopReceptionNoAssociationModal
              type={noAssociationModalInfo.type}
              isModalOpen={showNoAssocModal}
              closeModal={() => setShowNoAssocModal(false)}
              isOpen={showNoAssocModal}
              onClose={() => setShowNoAssocModal(false)}
            />
          )}

          <Layout modules={modulesWithDisabled} setSubdomainId={setSubdomainId}>
            <BackButtonTitle applicationPages={applicationPages} />
            <React.Suspense
              fallback={
                <div style={{ display: 'flex' }}>
                  <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
                </div>
              }
            >
              <Routes>
                <Route
                  path={URLS.AUTH_HOME}
                  element={
                    <PrivateRoute
                      component={Home}
                      modules={modulesWithDisabled}
                      setSubdomainId={setSubdomainId}
                    />
                  }
                />

                {governanceRoutes}
                {bakeryRoutes}
                {volumetryRoutes}
                {supplyOmniRoutes}

                {/* Shop Reception routes are not mounted if disabled */}
                {!shopReceptionDisabled && merchandiseRoutes}
                {!shopReceptionDisabled && shopReceptionRoutes}

                {lostSalesOpportunitiesRoutes}
                {locationRoutes}
                {wasteManagementRoutes}
                {employeeRoutes}
                {iamRoutes}
                {coraSGRRoutes}
                {employeeDiscountsRoutes}

                {applicationModulesPagesWithDisabled.map(page =>
                  createRoute(
                    {
                      ...page,
                      subdomainId,
                      modules: modulesWithDisabled,
                      applicationPages,
                      hasLoadedActivities,
                      isLoadingActivities
                    },
                    ServiceDomain
                  )
                )}

                <Route path={URLS.LOGINOIDC} element={<LoginOIDC />} />
                <Route path={URLS.LOGOUTOIDC} element={<LogoutOIDC />} />
                {applicationPages.length && <Route path="*" element={<NotFound />} />}
              </Routes>
            </React.Suspense>
          </Layout>
        </>
      )}
    </ThemeProvider>
  )
}

export default AppInner
